import { Box, Typography, Card, Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { calcGrowth, toBarSegments } from '@/helpers/analytics/charts.js';
import { formatNumber, formatPercent } from '@/helpers/format/formatNumber.js';
import { formatTimeInterval } from '@/helpers/date/formatTimeInterval.js';
import ProblemIcon from '@/assets/icons/problem-icon.svg?react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { BREAKPOINTS } from '@/constants/IndicatorBarBreakpoints';

export const StatisticCard = ({ title, value, isFetching, icon = false, link = false, tooltip, status = null }) => {
    const content = (
        <Box>
            <Typography>{title}</Typography>
            {status && !isFetching ? (
                <Box className='statistic-card-with-growth'>
                    <Typography variant='h3'>
                        {value}
                        {icon && <ProblemIcon />}
                    </Typography>
                    {status}
                </Box>
            ) : (
                <Typography variant='h3'>
                    {isFetching ? <CircularProgress /> : value}
                    {icon && <ProblemIcon />}
                </Typography>
            )}
        </Box>
    );

    const cardContent = tooltip ? (
        <Tooltip title={tooltip} arrow placement='bottom' classes={{ tooltip: 'tooltip-white' }}>
            {content}
        </Tooltip>
    ) : (
        content
    );

    return <Card className='statistic-card'>{link ? <Link to={link}>{cardContent}</Link> : cardContent}</Card>;
};

export const MultiStatisticCard = ({
    mainTitle,
    mainValue,
    firstTitle,
    firstValue,
    secondTitle,
    secondValue,
    firstIcon,
    secondIcon,
    thirdTitle,
    thirdValue,
    thirdIcon,
    link = false,
}) => {
    const content = (
        <Box>
            <Box className='main-info'>
                <Typography>{mainTitle}</Typography>
                <Typography variant='h3' component='h3'>
                    {mainValue}
                </Typography>
            </Box>
            <Box className='details-info' display='flex'>
                <Box className='item'>
                    <Box className='icon'>{firstIcon}</Box>
                    <Box className='content'>
                        <Typography>{firstTitle}</Typography>
                        <Typography variant='h4' component='h3'>
                            {firstValue}
                        </Typography>
                    </Box>
                </Box>
                <Box className='item'>
                    <Box className='icon'>{secondIcon}</Box>
                    <Box className='content'>
                        <Typography>{secondTitle}</Typography>
                        <Typography variant='h4' component='h3'>
                            {secondValue}
                        </Typography>
                    </Box>
                </Box>
                {thirdValue !== undefined && thirdValue !== null && (
                    <Box className='item'>
                        <Box className='icon'>{thirdIcon}</Box>
                        <Box className='content'>
                            <Typography>{thirdTitle}</Typography>
                            <Typography variant='h4' component='h3'>
                                {thirdValue}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
    return <Card className='statistic-card statistic-card--multiple'>{link ? <Link to={link}>{content}</Link> : content}</Card>;
};

export const StatisticCardWithGrowth = ({ title, current, previous, isFetching, format = 'number', digits = 0, reversed = false, link = false }) => {
    const growth = calcGrowth(current, previous);
    const value = formatValue(current, format, digits);

    const content = (
        <Box>
            <Typography>{title}</Typography>
            {isFetching ? (
                <CircularProgress />
            ) : (
                <Box className='statistic-card-with-growth'>
                    <Typography variant='h3' component='h3'>
                        {value}
                    </Typography>
                    <Growth value={growth} reverse={reversed} />
                </Box>
            )}
        </Box>
    );

    return <Card className='statistic-card'>{link ? <Link to={link}>{content}</Link> : content}</Card>;
};

export const StatisticCardWithBarIndicator = ({ title, isFetching, value, link = false }) => {
    const barColors = useBarIndicatorsColors(value, BREAKPOINTS);
    const barSegments = toBarSegments(barColors);
    const content = (
        <Box>
            <Typography>{title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography variant='h3'>{isFetching ? <CircularProgress /> : value + '%'}</Typography>
                <Box sx={{ display: 'flex', gap: '2px' }}>
                    {barSegments.map(segment => (
                        <Box
                            key={segment.key}
                            sx={{
                                width: '6px',
                                height: '24px',
                                borderRadius: '2px',
                                backgroundColor: segment.color,
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
    return <Card className='statistic-card'>{link ? <Link to={link}>{content}</Link> : content}</Card>;
};

const formatValue = (value, format, digits) => {
    if (format === 'time') {
        return formatTimeInterval(value);
    }

    if (format === 'percent') {
        return formatPercent(value, digits);
    }

    return formatNumber(value, digits);
};

StatisticCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.node]),
    isFetching: PropTypes.bool,
    icon: PropTypes.bool,
    link: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    tooltip: PropTypes.string,
    status: PropTypes.node,
};

StatisticCardWithGrowth.propTypes = {
    title: PropTypes.string.isRequired,
    current: PropTypes.number.isRequired,
    previous: PropTypes.number.isRequired,
    isFetching: PropTypes.bool.isRequired,
    format: PropTypes.oneOf(['number', 'percent', 'time']),
    digits: PropTypes.number,
    reversed: PropTypes.bool,
    link: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

MultiStatisticCard.propTypes = {
    mainTitle: PropTypes.string.isRequired,
    mainValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firstTitle: PropTypes.string.isRequired,
    firstValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firstIcon: PropTypes.node,
    secondTitle: PropTypes.string.isRequired,
    secondValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    secondIcon: PropTypes.node,
    thirdTitle: PropTypes.string,
    thirdValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    thirdIcon: PropTypes.node,
    link: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default StatisticCard;
